/* Gigglz: suggested inside messages, shared by the personalise page and Send List.
 * Data only, plus profile inference. No DOM.
 */
(function () {
  'use strict';
  // ============================================================
  // Suggest a message — data
  // Curated inside-card messages grouped by occasion "profile".
  // These are plain-text seed messages, kept here so they are
  // easy to maintain. They can later be moved to Shopify
  // metaobjects without changing any of the UI behaviour below.
  // Guidelines: no recipient name, no fixed age/relationship,
  // no gender assumptions, short enough for Medium on a 7x5 card.
  // ============================================================
  const SUGGEST_MESSAGES = {
    birthday: [
      'Happy birthday. You’re not old, you’re just aggressively experienced.',
      'Another year older, another year of pretending to have your life together.',
      'Happy birthday. I hope your cake is bigger than your responsibilities.',
      'Congratulations on surviving another trip around the sun.',
      'Happy birthday. May your day be full of cake, presents and people not asking awkward questions.',
      'Happy birthday to someone ageing like fine wine. Or at least like opened wine.',
      'Hope your birthday is as brilliant as you pretend to be online.',
      'You take the cake. Mostly because it’s your birthday and no one can stop you.',
      'Donut worry about getting older. Eat cake instead.',
      'Happy birthday. I was going to get you something expensive, then I remembered who you are.',
      'Another year older and still causing problems. Proud of you.',
      'Hope today brings cake, laughs and a reminder of how loved you are.',
      'Happy birthday. I’m very glad you exist.',
      'Hope this year brings you more joy, less stress and better snacks.',
      'Happy birthday. Officially older, suspiciously no wiser.',
      'Another year, another excuse to eat cake for breakfast.',
      'Happy birthday. You’ve aged like a fine cheese: rich, mature and slightly whiffy.',
      'Wishing you a birthday with good cake, better company and zero adulting.',
      'Happy birthday. Blow out the candles before the smoke alarm joins in.',
      'You’re not getting older, you’re just levelling up. Slowly.',
      'Happy birthday. Let’s pretend the number is a suggestion.',
      'Have a brilliant day. You’ve earned the big slice.',
      'Happy birthday. Same fabulous you, now with extra experience points.',
      'Wishing you cake, naps and absolutely no responsibilities today.',
      'Happy birthday. Go on, make a wish. Snacks count.',
      'Another year of being effortlessly great. Happy birthday.'
    ],
    anniversary_love: [
      'Still my favourite person to do absolutely nothing with.',
      'I’d choose you again. Probably faster this time.',
      'Happy anniversary to us, and to putting up with each other so well.',
      'You’re still the best decision I’ve made. Please don’t let it go to your head.',
      'Love you loads, even when you steal the duvet.',
      'Here’s to more years of snacks, sofa space and each other.',
      'Happy anniversary. You’re stuck with me, and I’m delighted about it.',
      'I love you more than cake. Not all cake, but definitely most cake.',
      'Thanks for being my favourite person to annoy forever.',
      'Still ridiculously happy I met you.',
      'You make ordinary days a lot better. Happy anniversary.',
      'To us: a bit chaotic, mostly brilliant, always worth it.',
      'Happy anniversary to the person I fancy most in any room.',
      'Every day with you is my favourite kind of ordinary.',
      'Happy anniversary. Still my favourite notification.',
      'You’re the best thing I’ve ever accidentally got right.',
      'Here’s to another year of shared snacks and stolen chips.',
      'Happy anniversary. I’d swipe right all over again.',
      'Turns out you’re not a phase. Happy anniversary.',
      'Still choosing you, even on the days you chew loudly.',
      'Happy anniversary to my favourite person and worst backseat driver.',
      'Happy anniversary. You’re my favourite habit.',
      'Years in and you’re still the best part of my day.',
      'Happy anniversary. Thanks for laughing at the same rubbish I do.',
      'Us against the world, or at least against the washing-up.',
      'Happy anniversary. I love you a stupid amount.'
    ],
    congratulations_exam: [
      'Congratulations. You did it!',
      'You worked hard and it paid off.',
      'You absolutely smashed it.',
      'Well done. You’ve properly earned this.',
      'Congratulations. Your brain has finally earned its keep.',
      'You passed. Pretending not to revise clearly worked.',
      'All those highlighters were worth it after all.',
      'You did it. Smugness is now fully permitted.',
      'Well done. You can safely forget most of it now.',
      'The revision is over. Long live the nap.',
      'So proud of you. Congratulations!',
      'Whatever the numbers say, we’re incredibly proud of how hard you worked.',
      'Congratulations, you absolute brainbox.',
      'You smashed it. Now go celebrate properly.',
      'Congratulations. Officially clever, officially unbearable.',
      'You revised, you survived, you smashed it.',
      'Well done. Time to delete the study timetable with pride.',
      'Congratulations. All that panic was clearly productive.',
      'Books closed, brain full, results glorious. Well done.',
      'You did the reading (mostly) and it worked. Congratulations.',
      'Certified brilliant. Please try to stay humble.',
      'Congratulations. Now go and forget everything guilt-free.',
      'Proof that stress and snacks can achieve great things. Well done.',
      'You earned this one. Go and celebrate loudly.',
      'Congratulations. The library can have its chair back now.',
      'Clever, capable and finally finished. Well done.'
    ],
    leaving: [
      'Good luck. They have no idea how much they’re going to miss you.',
      'Off you go then. Try not to be too brilliant somewhere else.',
      'It won’t be the same without you, and we’re a bit annoyed about it.',
      'Congratulations on escaping. We’re only slightly jealous.',
      'Good luck with the next adventure. You’ve earned it.',
      'Thanks for everything. The place won’t be the same.',
      'Wishing you all the best in whatever comes next.',
      'New chapter, same brilliant you. Go get it.',
      'Sad to see you go, delighted for where you’re going.',
      'Good luck. Don’t forget us when you’re famous.',
      'Enjoy every minute of what’s next. You deserve it.',
      'Here’s to fresh starts and slightly better coffee.',
      'Onwards. You’re going to be great, obviously.',
      'Thanks for the memories, the laughs and the covering for me. Good luck.',
      'Good luck. Their loss is very much someone else’s gain.',
      'Off to bigger things and better biscuits. Good luck.',
      'We’d say don’t go, but we’re thrilled for you. Mostly.',
      'New job, same legend. Go show them how it’s done.',
      'Good luck out there. Keep our seat warm in your memory.',
      'Thanks for everything. Try not to have too much fun without us.',
      'Wishing you a brilliant next chapter and a shorter commute.',
      'You’re off, and honestly, good for you. Smash it.',
      'Good luck. The group chat stays open, obviously.',
      'Onwards and upwards. Take the good pens with you.',
      'So long, and thanks for making this place bearable.',
      'Good luck. We fully expect a namecheck in the memoir.'
    ],
    thank_you: [
      'Thank you for everything. It really made a difference.',
      'Just a little note to say a big thank you.',
      'Thank you for being so kind, patient and brilliant.',
      'Thank you for all your care, encouragement and hard work.',
      'Thank you for making such a big difference.',
      'I really appreciate everything you’ve done.',
      'Thank you. You’re one of the good ones.',
      'Thank you for being brilliant, even on the wobbly days.',
      'A proper thank you, without going full emotional speech.',
      'Thank you for everything, including the bits I probably forgot to notice.',
      'Thank you for being so thoughtful. It doesn’t go unnoticed.',
      'This card is small but the gratitude is not. Thank you.',
      'Thank you for all the little things that turned out to be big things.',
      'Thank you, from the bottom of my slightly disorganised heart.',
      'Thank you. You made something hard feel a lot easier.',
      'Officially the kindest thing anyone’s done in ages. Thank you.',
      'Thank you for going out of your way. It meant a lot.',
      'A small card for a big thank you. You’re a star.',
      'Thank you for being reliably brilliant. It’s noticed.',
      'I owe you one, or possibly several. Thank you.',
      'Thank you for the effort, the patience and the good grace.',
      'You didn’t have to, and you did anyway. Thank you.',
      'Thank you. The world needs more people like you.',
      'Cheers for everything. You’re quietly wonderful.',
      'Thank you for showing up when it counted.',
      'Massive thanks. You made a real difference.'
    ],
    wedding: [
      'Congratulations. Here’s to a lifetime of love and mild bickering.',
      'Wishing you both all the happiness in the world.',
      'So happy for you both. Here’s to the two of you.',
      'Congratulations on finding your person. Lucky them, lucky you.',
      'To love, laughter and a very happy ever after.',
      'Wishing you a marriage full of joy, snacks and shared blankets.',
      'Congratulations. May your first argument be about something brilliant, like cake.',
      'Here’s to the two of you and everything ahead.',
      'So thrilled for you both. Have the most wonderful day.',
      'Wishing you a lifetime of love and never running out of things to laugh about.',
      'Congratulations on making it official. About time, and worth it.',
      'To the happy couple: enjoy every single moment.',
      'Love, laughter and happily ever after. Congratulations to you both.',
      'Wishing you both a wonderful wedding and an even better marriage.',
      'Congratulations. May your love last longer than the leftovers.',
      'To the newlyweds: here’s to forever, and to sharing the duvet.',
      'Wishing you both a lifetime of good days and shared puddings.',
      'Congratulations. You found a keeper. Well done, both of you.',
      'Here’s to matching rings and mismatched socks. Congratulations.',
      'So happy for you two. Go and be brilliant together.',
      'Congratulations on the wedding. Now the fun admin begins.',
      'May your marriage be as strong as your morning coffee.',
      'Two people, one team, endless snacks. Congratulations.',
      'Wishing you a marriage full of laughter and very few in-law dramas.',
      'Congratulations. Here’s to always being each other’s favourite.',
      'To love and putting up with each other beautifully. Congratulations.'
    ],
    christmas: [
      'Merry Christmas and a Happy New Year.',
      'Hope your Christmas is full of food, rest and very few awkward conversations.',
      'Merry Christmas. Eat, drink and avoid doing the washing up.',
      'Merry Christmas. Hope your tree stays upright and your relatives behave.',
      'Wishing you peace, joy and batteries that are actually included.',
      'Merry Christmas. May your sprouts be edible and your board games brief.',
      'Merry Christmas. I hope Santa brings you patience. And wine.',
      'Here’s to good food, bad jokes and at least one person asleep by 3pm.',
      'Merry Christmas. I got you this card because I’m thoughtful and financially sensible.',
      'Hope your Christmas is full of love, laughter and very low expectations.',
      'Wishing you a festive season full of good food and better company.',
      'Hope your Christmas is merry, bright and only mildly chaotic.',
      'Merry Christmas. May your family drama be short and your leftovers plentiful.',
      'Sending lots of love this Christmas.',
      'Merry Christmas. May your wifi be strong and your relatives mild.',
      'Wishing you a cosy Christmas and a suspiciously large cheeseboard.',
      'Merry Christmas. Nap hard, eat harder.',
      'Hope your Christmas is merry, bright and heavy on the roast potatoes.',
      'Merry Christmas. May the good tin of chocolates come to you first.',
      'Wishing you festive joy and a remote control you don’t have to share.',
      'Merry Christmas. Here’s to peace, goodwill and second helpings.',
      'Hope your Christmas is warm, bright and gloriously lazy.',
      'Merry Christmas. May your sprouts be optional.',
      'Wishing you all the cosy nights and none of the queues.',
      'Merry Christmas. Eat like it’s your job.',
      'Season’s greetings, and may your batteries be included this year.'
    ],
    // Business lists: clients, suppliers and colleagues. Warm and a bit
    // human, never rude; still short enough for Medium on a 7x5 card.
    business_thanks: [
      'Thank you for your business this year. It’s been a pleasure working with you.',
      'A proper thank you for your support. We really do appreciate it.',
      'Thank you for trusting us with your work. We don’t take it for granted.',
      'Thanks for being brilliant to work with, even on the busy days.',
      'Thank you for another great year together. Here’s to the next one.',
      'Just a note to say thank you. You made our year easier and better.',
      'Thank you for choosing us. It means a lot to a small team like ours.',
      'Thank you for the referrals, the patience and the prompt replies.',
      'With thanks for your support, your custom and your good humour.',
      'Thank you. Working with you has been one of the highlights of our year.',
      'A small card to say a big thank you from all of us.',
      'Thank you for your hard work and for making the job more fun.'
    ],
    business_christmas: [
      'Wishing you a very happy Christmas and a prosperous New Year.',
      'Season’s greetings and thank you for your support this year.',
      'Merry Christmas from all of us, and thank you for a great year.',
      'Wishing you a restful Christmas and a strong start to the New Year.',
      'Thank you for working with us this year. Have a wonderful Christmas.',
      'Merry Christmas. May your inbox be quiet and your mince pies plentiful.',
      'Season’s greetings. We hope the break is long and the out-of-office firmly on.',
      'Wishing you a happy Christmas and a New Year with fewer meetings.',
      'Thank you for your custom this year. Have a lovely, well-earned break.',
      'Merry Christmas. Here’s to switching off properly for once.',
      'With warm wishes for Christmas and every success in the year ahead.',
      'Happy Christmas, and thanks for being great to work with.'
    ],
    general: [
      'Thinking of you and hoping this made you smile.',
      'Just because. Hope you’re doing brilliantly.',
      'Sending a little something to brighten your day.',
      'Hope this card finds you well, or at least well-fed.',
      'A tiny paper hello, because you popped into my head.',
      'Hope your day is as lovely as you are.',
      'Wishing you good things, better snacks and fewer awkward meetings.',
      'Just a note to say you’re pretty great.',
      'Sending love, and possibly bad jokes, your way.',
      'Hope this brings a smile. That’s the whole plan.',
      'Thinking of you. No occasion required.',
      'Here’s a card, because you deserve nice things.',
      'Wishing you a brilliant day and an even better week.',
      'You’re one of the good ones. Just thought you should know.',
      'No reason, just a card to say hello.',
      'Hope today’s treating you kindly.',
      'Sending good vibes and mediocre handwriting.',
      'Just a little something to say you’re appreciated.',
      'Thought of you, so here’s some paper proof.',
      'Hope your week is full of small, excellent moments.',
      'You’re brilliant. Carry on.',
      'A card for no reason is the best kind of card.',
      'Sending a smile, wrapped in an envelope.',
      'Hope this lands on a good day. If not, here’s a better one.',
      'Just checking in with maximum affection.',
      'Here’s to you, for absolutely no occasion at all.'
    ],
    fathers_day: [
      'Happy Father’s Day. Thanks for the lifts, the lectures and the surprisingly good advice.',
      'Happy Father’s Day to the man who taught me most of what I pretend I already knew.',
      'Happy Father’s Day. Still the best at barbecues and questionable DIY.',
      'Thanks for being steady, patient and only slightly embarrassing. Happy Father’s Day.',
      'Happy Father’s Day. I turned out alright, so clearly you did something right.',
      'Happy Father’s Day to a proper legend. Put your feet up, you’ve earned it.',
      'Thanks for all the lifts, snacks and unsolicited opinions. Happy Father’s Day.',
      'You’re a brilliant dad and a world-class nap enthusiast. Happy Father’s Day.',
      'Happy Father’s Day. Thanks for pretending my jokes are funny. I get it from somewhere.',
      'Happy Father’s Day to the human toolbox, taxi and cashpoint. Love you loads.',
      'Thanks for everything, especially the bits I forget to say thank you for. Happy Father’s Day.',
      'Happy Father’s Day. Enjoy a day off from being asked to fix things.',
      'Happy Father’s Day. Sorry in advance for whatever I break next.',
      'Happy Father’s Day. Thanks for the dad jokes I now tell too.',
      'Happy Father’s Day to the original problem-solver and snack-finder.',
      'Cheers for the wisdom, the lifts and the endless phone advice. Happy Father’s Day.',
      'Happy Father’s Day. You’re basically a superhero with better parking.',
      'Thanks for always answering the daft questions. Happy Father’s Day.',
      'Happy Father’s Day. Sorry for the teenage years, mostly.',
      'To the king of the shed and the barbecue. Happy Father’s Day.',
      'Happy Father’s Day. Still the person I call when something breaks.',
      'Thanks for the calm, the patience and the terrible puns. Happy Father’s Day.',
      'Happy Father’s Day. You made growing up feel safe and slightly hilarious.',
      'Legend. Icon. Dad. Happy Father’s Day.',
      'Happy Father’s Day. Put your feet up and let someone else do the driving.'
    ],
    mothers_day: [
      'Happy Mother’s Day. Thanks for the patience and the occasional telling-off I probably deserved.',
      'Happy Mother’s Day to the person who holds it all together, usually while making it look easy.',
      'Thanks for everything, especially the bits I never noticed at the time. Happy Mother’s Day.',
      'Happy Mother’s Day. I turned out pretty well, so take the credit.',
      'Happy Mother’s Day to a proper legend. Put the kettle on and let someone else run around.',
      'Thanks for the lifts, the snacks and the wisdom I pretend I already had. Happy Mother’s Day.',
      'Happy Mother’s Day. Sorry for the grey hairs, love you loads.',
      'You make it all look effortless, which we know it isn’t. Happy Mother’s Day.',
      'Happy Mother’s Day. Thanks for always being in my corner, even when I’m wrong.',
      'Happy Mother’s Day to the best cook, counsellor and emergency contact going.',
      'Thanks for everything you do, and everything you did before we appreciated it. Happy Mother’s Day.',
      'Happy Mother’s Day. Have a proper rest, you’ve more than earned it.',
      'Happy Mother’s Day. You’re brilliant, and not just because you have to be.',
      'Happy Mother’s Day. Thanks for the wisdom I ignored and later used.',
      'Happy Mother’s Day to the woman who can find anything in any bag.',
      'Thanks for the patience, the snacks and the endless good advice. Happy Mother’s Day.',
      'Happy Mother’s Day. You’re basically a superhero with better multitasking.',
      'Cheers for always picking up the phone. Happy Mother’s Day.',
      'Happy Mother’s Day. Sorry about the teenage years, truly.',
      'To the household CEO and chief snack officer. Happy Mother’s Day.',
      'Happy Mother’s Day. Still the first person I call about everything.',
      'Thanks for the calm in the chaos. Happy Mother’s Day.',
      'Happy Mother’s Day. You made everything feel manageable and a bit magic.',
      'Legend. Icon. Mum. Happy Mother’s Day.',
      'Happy Mother’s Day. Feet up, tea in hand, day off from us.'
    ],
    halloween: [
      'Happy Halloween. Eat the sweets, blame the ghosts.',
      'Wishing you a spooky one. Mind the goblins and the gin.',
      'Happy Halloween. May your costume be great and your hangover survivable.',
      'Have a wickedly good Halloween. Steal sweets responsibly.',
      'Happy Halloween. The scariest thing this year is still the group chat.',
      'Wishing you frights, treats and just the right amount of chaos.',
      'Happy Halloween. Get spooky, get snacks, regret nothing.',
      'Have a haunting good one. Try not to summon anything you can’t handle.',
      'Happy Halloween. The costume’s optional, the sweets are compulsory.',
      'Wishing you a Halloween full of tricks, treats and terrible decisions.',
      'Happy Halloween. Boo. That’s it, that’s the card.',
      'Have a fang-tastic Halloween. Sorry, I had to.',
      'Happy Halloween. May your bucket be full and your neighbours generous.',
      'Happy Halloween. Spooky season, zero chill.',
      'Wishing you a night of sugar highs and questionable costumes.',
      'Happy Halloween. May your treats outnumber your tricks.',
      'Have a frightfully good time. Snacks first, screaming later.',
      'Happy Halloween. Ghoul’s night out, obviously.',
      'Wishing you cobwebs, chaos and far too many sweets.',
      'Happy Halloween. Keep it creepy, keep it fun.',
      'May your pumpkins be smug and your sweets be plentiful.',
      'Happy Halloween. Dress up, act up, sugar up.',
      'Wishing you a suitably spooky, mildly ridiculous night.',
      'Happy Halloween. The scariest bit is running out of chocolate.',
      'Boo and best wishes. Have a wickedly good one.'
    ],
    easter: [
      "Happy Easter. Eat the chocolate, ignore the calendar.",
      "Wishing you eggs, chocolate and a well-earned lie-in.",
      "Happy Easter. May your eggs be plentiful and your willpower absent.",
      "Have an egg-cellent Easter. Sorry, someone had to.",
      "Happy Easter. Chocolate for breakfast is basically tradition now.",
      "Wishing you a hoppy Easter and absolutely no sharing.",
      "Happy Easter. Hide the eggs, guard the good chocolate.",
      "May your Easter be sweet and your chocolate stash secret.",
      "Happy Easter. Go on, the big egg counts as one portion.",
      "Wishing you a cracking Easter. Pun fully intended.",
      "Happy Easter. All the chocolate, none of the guilt.",
      "Have a lovely Easter. The bunny has been generous this year.",
      "Happy Easter. Eggs first, questions later.",
      "Wishing you sunshine, chocolate and a proper sit-down.",
      "Happy Easter. Treat yourself, you absolute good egg.",
      "May your Easter be full of chocolate and empty of plans.",
      "Happy Easter. Long weekend, short attention span, endless snacks.",
      "Wishing you a happy, chocolatey, slightly overindulgent Easter.",
    ],
    grandparents: [
      'Thanks for the endless snacks, stories and second helpings of everything.',
      'You give the best cuddles and the worst-kept sweet stash. Love you loads.',
      'Thanks for spoiling us rotten and pretending it’s a secret.',
      'You’re the fun one, and everyone knows it.',
      'Thanks for always having biscuits, time and terrible jokes ready.',
      'You make everything feel a bit warmer and a lot funnier. Thank you.',
      'Thanks for the stories we’ve heard a hundred times and still love.',
      'You’re a proper legend, and the snacks help too.',
      'Thanks for the wisdom, the treats and the occasional bit of mischief.',
      'You spoil us, you feed us, you put up with us. Thank you for everything.',
      'Thanks for being brilliant, generous and quietly in charge of everything.',
      'You give the best hugs and the strongest cups of tea. Love you.',
      'Thanks for everything you do, and for always saving us the good biscuits.',
      'Thanks for the sweets, the stories and the always-open biscuit tin.',
      'You’re the calm, the cuddles and the extra pudding. Thank you.',
      'Thanks for spoiling us and denying it beautifully.',
      'Nobody does snacks, naps and wisdom quite like you.',
      'Thanks for the endless patience and the secret treats.',
      'You make everything cosier just by being there. Thank you.',
      'Thanks for the tall tales and the taller portions.',
      'You’re a legend, and the biscuit supply proves it.',
      'Thanks for the hugs, the history and the good gossip.',
      'You spoil us rotten and we love you for it.',
      'Thanks for always having time, tea and a spare fiver.',
      'You’re simply the best, and you have the best snacks.'
    ],
    new_home: [
      'Congratulations on the new place. May the boxes unpack themselves.',
      'Welcome home. Here’s to good times and slightly better wifi.',
      'Congratulations on the move. Everything is now officially your problem.',
      'Happy new home. May it be full of laughter, snacks and very few leaks.',
      'Congratulations. Drill a hole wherever you like, it’s yours now.',
      'Welcome to the new place. Bring wine, ignore the boxes.',
      'Congratulations on the keys. Here’s to making it properly yours.',
      'Happy housewarming. May your neighbours be quiet and your radiators work.',
      'Congratulations on the new home. Try not to argue about the sofa.',
      'Here’s to new beginnings, fresh paint and finally finding the box with the kettle.',
      'Welcome home. May it be cosy, chaotic and completely yours.',
      'Congratulations on the move. Adulthood looks good on you, mostly.',
      'Happy new home. Fill it with good people and better snacks.',
      'Congratulations on the keys. Now the fun of finding the light switches begins.',
      'Welcome home. May the boxes be few and the takeaways be many.',
      'Congratulations. New address, same excellent taste.',
      'Here’s to your new place and the first cup of tea in it.',
      'Congratulations on the move. Try to enjoy the chaos.',
      'Welcome home. May your neighbours be kind and your wifi be instant.',
      'Congratulations. A new home, and a fresh set of things to argue over.',
      'Happy new home. Fill it with laughter and questionable furniture choices.',
      'Congratulations on the move. The hard part (the packing) is over.',
      'Here’s to new beginnings and finally having your own front door.',
      'Welcome home. May every room feel like yours in no time.',
      'Congratulations. Housewarming invite pending, obviously.'
    ],
    thinking_of_you: [
      'Just thinking of you and hoping today’s a kinder one.',
      'Sending a bit of love your way. No occasion, just because.',
      'Thinking of you. Take it steady, you’re doing brilliantly.',
      'Hope you’re being gentle with yourself. Here if you need me.',
      'Just a little note to say you’re on my mind.',
      'Sending you strength, snacks and a big virtual hug.',
      'Thinking of you and hoping things feel lighter soon.',
      'No big words, just lots of care coming your way.',
      'Hope this brings a small smile on a tricky day.',
      'You’ve got this, and you’ve got me. Thinking of you.',
      'Sending love and a reminder that you’re not on your own.',
      'Just because you’re brilliant and today felt like a good day to say it.',
      'Thinking of you. Rest up, take your time, no rush.',
      'Thinking of you and sending a little extra kindness today.',
      'Just a note to say I’m here, whenever you need.',
      'Hope today feels a bit gentler. Thinking of you.',
      'Sending calm, comfort and a big virtual cuppa.',
      'You’re in my thoughts more than you know.',
      'Take it one small step at a time. Thinking of you.',
      'Sending love and zero pressure to reply.',
      'Hope you can feel how much people are rooting for you.',
      'Just here, thinking of you and hoping for brighter days.',
      'Be kind to yourself today. You’re doing enough.',
      'Sending warmth your way, no strings attached.',
      'Thinking of you, quietly and often.'
    ],
    new_baby: [
      'Congratulations on the new arrival. Sleep is cancelled, but it’s worth it.',
      'Welcome to the world, tiny human. Good luck to everyone involved.',
      'Congratulations. You made a whole person, which is frankly showing off.',
      'Wishing you cuddles, coffee and the occasional full night’s sleep.',
      'Congratulations on the baby. Nappies now, chaos forever, love always.',
      'Welcome, little one. Your grown-ups are delighted and slightly terrified.',
      'Congratulations. Here’s to tiny socks and enormous love.',
      'Sending love to the new arrival and the very tired grown-ups.',
      'Congratulations. Sleep when you can, cuddle whenever you like.',
      'Welcome to the world. It’s a lot, but the snacks are good.',
      'Congratulations on your new tiny boss. Enjoy every exhausting minute.',
      'Wishing your growing family all the love, and a lot of caffeine.',
      'Congratulations. One more person to love, and one less lie-in.',
      'Congratulations. A tiny human, an enormous amount of love.',
      'Welcome to the world, little one. The chaos starts now.',
      'Congratulations on the new arrival and your new alarm clock.',
      'Sending love to the whole sleepy, happy crew.',
      'Congratulations. Tiny socks, huge feelings.',
      'Welcome, little one. Your grown-ups are smitten and shattered.',
      'Congratulations. May the naps be long and the nappies be brief.',
      'A whole new person to love. Congratulations.',
      'Welcome to the world. Snacks and cuddles all round.',
      'Congratulations on your tiny new boss.',
      'Wishing the new arrival a lifetime of love and laughter.',
      'Congratulations. Sleep now sold separately.'
    ]
  };

  // Keyword rules for inferring the profile from page context
  // (front_asset_key / product handle / return URL). Ordered so
  // more specific occasions win over broader ones.
  const SUGGEST_PROFILE_RULES = [
    { profile: 'wedding', keywords: ['wedding', 'married', 'marriage', 'engagement', 'engaged', 'bride', 'groom', 'newlywed'] },
    { profile: 'anniversary_love', keywords: ['anniversary', 'valentine', 'valentines'] },
    { profile: 'halloween', keywords: ['halloween', 'spooky', 'pumpkin', 'haunt', 'ghoul', 'witch', 'ghost'] },
    { profile: 'christmas', keywords: ['christmas', 'xmas', 'santa', 'festive', 'noel', 'advent'] },
    { profile: 'easter', keywords:['easter','egg','eggs','bunny','chick']},
    { profile: 'fathers_day', keywords: ['fathers-day', 'father'] },
    { profile: 'mothers_day', keywords: ['mothers-day', 'mother'] },
    { profile: 'grandparents', keywords: ['grandparents-day', 'grandparents'] },
    { profile: 'new_home', keywords: ['new-home', 'housewarming', 'new-place', 'moving-in', 'first-home'] },
    { profile: 'new_baby', keywords: ['new-baby', 'new-parents', 'baby'] },
    { profile: 'leaving', keywords: ['leaving', 'retirement', 'retiring', 'new-job', 'newjob', 'good-luck', 'goodluck', 'farewell'] },
    { profile: 'congratulations_exam', keywords: ['exam', 'exams', 'gcse', 'gcses', 'a-level', 'a-levels', 'alevel', 'graduation', 'graduate', 'results', 'passed', 'congratulations', 'congrats', 'degree'] },
    { profile: 'thank_you', keywords: ['thank', 'thanks', 'teacher', 'teaching-assistant'] },
    { profile: 'thinking_of_you', keywords: ['thinking-of-you', 'get-well', 'miss-you', 'missing-you', 'just-because'] },
    { profile: 'birthday', keywords: ['birthday', 'bday'] }
  ];

  const SUGGEST_DEFAULT_PROFILE = 'general';
  const SUGGEST_SS_PREFIX = 'gigglz_suggest_v1_';

  // Resolve a message profile. Priority: explicit > product metafield > inferred from context > general.
  function resolveProfile(explicit, fromProduct, contextString) {
    const e = String(explicit || '').trim().toLowerCase();
    if (e && SUGGEST_MESSAGES[e]) return e;
    const f = String(fromProduct || '').trim().toLowerCase();
    if (f && SUGGEST_MESSAGES[f]) return f;
    const ctx = String(contextString || '').toLowerCase();
    for (let i = 0; i < SUGGEST_PROFILE_RULES.length; i++) {
      const rule = SUGGEST_PROFILE_RULES[i];
      for (let k = 0; k < rule.keywords.length; k++) {
        if (ctx.indexOf(rule.keywords[k]) !== -1) return rule.profile;
      }
    }
    return SUGGEST_DEFAULT_PROFILE;
  }

  window.GigglzSuggestMessages = Object.freeze({
    SUGGEST_MESSAGES, SUGGEST_PROFILE_RULES, SUGGEST_DEFAULT_PROFILE, SUGGEST_SS_PREFIX, resolveProfile
  });
})();
